"use client";

import { useEffect, useState } from "react";
import Link from "next/link";

interface UpcomingEvent {
  name: string;
  slug: string;
  startDate: string;
  location: { city: string; state?: string; country: string };
  eventType: string;
}

function getCountdown(startDate: string) {
  const now = new Date();
  const event = new Date(startDate);
  const diff = event.getTime() - now.getTime();
  if (diff <= 0) return null;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  return { days, hours };
}

function CountdownBadge({ startDate }: { startDate: string }) {
  const [countdown, setCountdown] = useState<{ days: number; hours: number } | null>(null);

  useEffect(() => {
    setCountdown(getCountdown(startDate));
    const interval = setInterval(() => {
      setCountdown(getCountdown(startDate));
    }, 60_000);
    return () => clearInterval(interval);
  }, [startDate]);

  if (!countdown) return null;

  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
      {countdown.days}d {countdown.hours}h
    </span>
  );
}

function formatLocation(loc: { city: string; state?: string; country: string }) {
  const parts = [loc.city];
  if (loc.state) parts.push(loc.state);
  if (loc.country !== "USA") parts.push(loc.country);
  return parts.join(", ");
}

export function UpcomingEvents({ events }: { events: UpcomingEvent[] }) {
  if (events.length === 0) return null;

  return (
    <section className="mx-auto max-w-6xl px-4 py-12">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Upcoming Events
        </h2>
        <Link
          href="/events"
          className="text-sm font-medium text-primary hover:underline"
        >
          View all
        </Link>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {events.map((event) => (
          <Link
            key={event.slug}
            href={`/events/${event.slug}`}
            className="group flex items-start gap-3 rounded-lg border border-gray-200 p-4 transition-colors hover:border-primary/50 hover:bg-blue-50/50 dark:border-gray-700 dark:hover:border-primary/50 dark:hover:bg-gray-800/50"
          >
            <div className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-lg bg-primary/10 text-primary">
              <span className="text-xs font-medium leading-none">
                {new Date(event.startDate).toLocaleDateString("en-US", { month: "short", timeZone: "UTC" })}
              </span>
              <span className="text-lg font-bold leading-none">
                {new Date(event.startDate).getUTCDate()}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h3 className="truncate text-sm font-semibold text-gray-900 group-hover:text-primary dark:text-gray-100">
                  {event.name}
                </h3>
                <CountdownBadge startDate={event.startDate} />
              </div>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                {formatLocation(event.location)}
              </p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
